import { useEffect, useState } from 'react';
import { sessionsApi } from '../../../../api/sessionsApi';
import { tallestItemHeight } from '../../../../hooks/useFittingPageSize';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { usePagedList } from '../../../../hooks/usePagedList';
import { filterParams } from '../../../../utils/listFilters';
import { NO_SESSION_FILTERS } from '../components/SessionFilters';

const SEARCH_DEBOUNCE_MS = 300;

// Used for the very first request, before the table is on screen.
// Mirrors the fixed row height and the header of .session-table in session-table.css.
const TABLE_FALLBACK = { itemHeight: 52, overhead: 30 };

const readTableLayout = (area) => {
  const head = area.querySelector('.session-table thead');
  const rows = area.querySelectorAll('.session-table tbody tr');
  // Rows have a fixed height, so the tallest one is the row height.
  const itemHeight = tallestItemHeight(rows);
  if (!head || !itemHeight) return null;

  return {
    itemHeight,
    // The header sticks to the top of the scroller, so it is never scrolled away.
    overhead: head.getBoundingClientRect().height,
  };
};

const normalizeStatus = (status) => String(status || 'UNKNOWN').toUpperCase();
const arrayOrEmpty = (value) => (Array.isArray(value) ? value : []);
const byLabel = (a, b) => a[1].localeCompare(b[1]);

/** Facet values for the filter selects: statuses, sorted `[id, title]` events, sorted users and models. */
const toFilterFacets = (data) => ({
  statuses: arrayOrEmpty(data?.statuses).map(normalizeStatus),
  events: arrayOrEmpty(data?.events)
    .map((e) => [String(e.id ?? e.event_id ?? ''), e.title ?? e.name ?? ''])
    .filter(([id, title]) => id && title)
    .sort(byLabel),
  users: [...arrayOrEmpty(data?.users)].sort(),
  models: [...arrayOrEmpty(data?.models)].sort(),
});

const EMPTY_FACETS = toFilterFacets(null);

const useSessionFacets = () => {
  const [facets, setFacets] = useState(EMPTY_FACETS);

  useEffect(() => {
    let cancelled = false;
    sessionsApi.facets()
      .then((data) => { if (!cancelled) setFacets(toFilterFacets(data)); })
      .catch((err) => console.error('Failed to load session facets:', err));
    return () => { cancelled = true; };
  }, []);

  return facets;
};

const sessionsOf = (data) => {
  if (Array.isArray(data?.sessions)) return data.sessions;
  if (Array.isArray(data?.results)) return data.results;
  return arrayOrEmpty(data);
};

/** Search, filters and sort for the sessions table, and the page of sessions that fits the screen. */
export function useSessionList() {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(NO_SESSION_FILTERS);
  const [sort, setSort] = useState('newest');
  const search = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const facets = useSessionFacets();

  const list = usePagedList({
    listKey: 'sessions',
    queryKey: JSON.stringify([search, filters, sort]),
    fetchPage: async ({ page, perPage }) => {
      const data = await sessionsApi.list({
        page,
        per_page: perPage,
        include_total: true,
        compact: true,
        search: search || undefined,
        ...filterParams(filters),
        sort,
      });
      return { items: sessionsOf(data), total: data?.total };
    },
    getLayout: readTableLayout,
    fallbackLayout: TABLE_FALLBACK,
    errorMessage: 'Failed to load sessions. Please try again later.',
  });

  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));
  const clearFilters = () => setFilters(NO_SESSION_FILTERS);

  return { ...list, query, setQuery, filters, setFilter, clearFilters, sort, setSort, facets };
}
